import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Card from '../../components/ui/Card';
import SafeIcon from '../../common/SafeIcon';
import Button from '../../components/ui/Button';
import { Navigate } from 'react-router-dom';
import { api } from '../../api/nocode';

export default function Bunkmate() {
  const { user, setUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [bunkmate, setBunkmate] = useState(null);
  const [fetchingBunkmate, setFetchingBunkmate] = useState(true);
  
  useEffect(() => {
    const fetchBunkmate = async () => {
      if (user?.bunkmate_id) {
        try {
          const res = await api.get('students_gwaacademy', { id: user.bunkmate_id });
          const data = Array.isArray(res) ? res : res.data || [];
          if (data.length > 0) {
            setBunkmate(data[0]);
          }
        } catch (err) {
          console.error("Failed to fetch bunkmate", err);
        }
      }
      setFetchingBunkmate(false);
    };

    if (user && user.pass_type === 'allaccess') {
      fetchBunkmate();
    } else {
      setFetchingBunkmate(false);
    }
  }, [user]);

  if (user?.pass_type !== 'allaccess') return <Navigate to="/upgrade" />;

  const handleToggle = async (value) => {
    setLoading(true);
    try {
      await api.put('students_gwaacademy', user.id, { wants_bunkmate: value });
      setUser({ ...user, wants_bunkmate: value });
    } catch (err) {
      console.error("Failed to update bunkmate preference", err);
      alert("Failed to update your preference. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const wantsBunkmate = user?.wants_bunkmate === 1;
  const hasAnswered = user?.wants_bunkmate !== null && user?.wants_bunkmate !== undefined;

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn">
       <div className="bg-[#EAF1E6] border-l-4 border-forest-green p-4 rounded-r-md">
        <h1 className="text-2xl font-bold text-dark-green flex items-center gap-3">
          <SafeIcon name="UserPlus" /> Your Lab Partner
        </h1>
      </div>

      {fetchingBunkmate ? (
        <Card className="text-center py-12">
          <SafeIcon name="Loader" className="animate-spin text-4xl text-amber mx-auto mb-4" />
          <p className="text-muted">Loading lab partner status...</p>
        </Card>
      ) : bunkmate ? (
        <Card className="flex flex-col md:flex-row gap-8 items-center md:items-start animate-fadeIn shadow-sm border-tan">
          <div className="w-32 h-32 bg-tan rounded-full flex items-center justify-center text-4xl text-forest-green shrink-0 shadow-inner">
            <SafeIcon name="User" />
          </div>
          <div className="space-y-4 flex-1 text-center md:text-left">
            <div>
              <h2 className="text-2xl font-bold text-dark-green">{bunkmate.name}</h2>
              <p className="text-muted">Your Academy Lab Partner</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-cream p-3 rounded-md border border-tan">
                <div className="text-xs text-muted uppercase font-semibold">Genre</div>
                <div className="font-medium text-dark-green capitalize">{bunkmate.genre || 'Not specified'}</div>
              </div>
              <div className="bg-cream p-3 rounded-md border border-tan">
                <div className="text-xs text-muted uppercase font-semibold">Stage</div>
                <div className="font-medium text-dark-green capitalize">{bunkmate.journey_stage?.replace('_', ' ') || 'Not specified'}</div>
              </div>
            </div>

            <div className="bg-amber/10 p-4 rounded-md border border-amber/30 text-sm text-dark-green">
              <span className="font-bold block mb-1">Intro Prompt:</span>
              Reach out to {bunkmate.name.split(' ')[0]} via email at <a href={`mailto:${bunkmate.email}`} className="text-forest-green font-bold hover:underline">{bunkmate.email}</a> and introduce yourself! Maybe share a little about yourself, what you have written or are working on, where you're at in your author journey and one goal you have for this week.
            </div>
          </div>
        </Card>
      ) : (
        <Card className="text-center py-12 px-6 animate-fadeIn shadow-sm border-tan">
          <div className="w-24 h-24 bg-amber/20 rounded-full flex items-center justify-center text-4xl text-amber mx-auto mb-6">
            <SafeIcon name="Users" />
          </div>
          
          <h2 className="text-2xl font-bold text-dark-green mb-4">
            Lab Partner Pairing Coming Soon!
          </h2>
          
          <p className="text-lg text-muted mb-8 max-w-2xl mx-auto leading-relaxed">
            Students will be paired over the coming weekend. Once the academy directors have finalized the pairings, you'll be able to see your lab partner's name, email address, genre, and stage right here so you can connect!
          </p>

          <div className="bg-cream border border-tan rounded-md p-6 max-w-xl mx-auto text-left shadow-sm">
            <h3 className="font-bold text-dark-green text-lg mb-2 flex items-center gap-2">
              <SafeIcon name="CheckCircle" className="text-forest-green" /> Permission to Pair
            </h3>
            <p className="text-sm text-muted mb-6">
              Please let us know if you'd like to be paired with a lab partner for the academy. 
              <strong className="block mt-2 text-amber font-bold">Note: Please confirm your preference by noon on Saturday, July 11.</strong>
            </p>

            {!hasAnswered ? (
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => handleToggle(1)} 
                  disabled={loading}
                  className="flex-1 bg-forest-green hover:bg-dark-green py-3 text-lg shadow-sm"
                >
                  {loading ? 'Saving...' : 'Yes, pair me up!'}
                </Button>
                <Button 
                  onClick={() => handleToggle(0)} 
                  disabled={loading}
                  variant="outline"
                  className="flex-1 border-tan text-muted hover:text-dark-green hover:bg-gray-50 py-3 text-lg"
                >
                  {loading ? 'Saving...' : "No, I'll write solo"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className={`p-4 rounded-md border flex items-start gap-3 transition-colors ${wantsBunkmate ? 'bg-[#EAF1E6] border-forest-green/30' : 'bg-gray-50 border-gray-200'}`}>
                  <SafeIcon name={wantsBunkmate ? "Check" : "X"} className={`text-xl ${wantsBunkmate ? 'text-forest-green' : 'text-gray-400'} mt-0.5`} />
                  <div>
                    <div className="font-bold text-dark-green">
                      {wantsBunkmate ? "You're on the list!" : "Writing Solo"}
                    </div>
                    <div className="text-sm text-muted mt-1">
                      {wantsBunkmate 
                        ? "You have opted in to be paired with a lab partner. Check back soon for your pairing." 
                        : "You have opted out of being paired with a lab partner for this academy session."}
                    </div>
                  </div>
                </div>
                
                <div className="text-center pt-2">
                  <button 
                    onClick={() => handleToggle(wantsBunkmate ? 0 : 1)}
                    disabled={loading}
                    className="text-sm text-amber hover:text-orange-600 font-medium underline transition-colors"
                  >
                    {loading ? 'Updating...' : 'Change my preference'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}